import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, PencilSimple, MapPin, MagnifyingGlass, Phone, EnvelopeSimple, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import ImportDialog from "@/components/ImportDialog";
import ClientDialog from "@/components/ClientDialog";
import { toast } from "sonner";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = () => api.listClients(search).then(setClients).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setOpen(true); };

  const allTags = [...new Set(clients.flatMap((c) => c.tags || []))].sort();
  const shown = tagFilter === "all" ? clients : clients.filter((c) => (c.tags || []).includes(tagFilter));

  const deleteAll = async () => {
    setDeleting(true);
    try {
      const r = await api.deleteAllClients();
      toast.success(`Deleted ${r.clients} clients, ${r.quotes} quotes, ${r.jobs} jobs, ${r.invoices} invoices`);
      setDeleteAllOpen(false); setConfirmText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.deleteClient(c.id); toast.success("Client deleted"); load();
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <PageHeader title="Clients" subtitle={`${clients.length} customers`} actions={
        <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end">
          <Button variant="outline" onClick={() => api.downloadCsv("clients").catch(() => toast.error("Export failed"))} data-testid="export-clients-csv"><DownloadSimple size={17} className="mr-1.5" /> Export CSV</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-clients-button"><UploadSimple size={17} className="mr-1.5" /> Import CSV</Button>
          {clients.length > 0 && <Button variant="outline" onClick={() => { setConfirmText(""); setDeleteAllOpen(true); }} data-testid="delete-all-clients-button" className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"><Trash size={17} className="mr-1.5" /> Delete All</Button>}
          <Button onClick={openNew} className="bg-[#0B1726] hover:bg-slate-800" data-testid="add-client-button"><Plus size={18} className="mr-1" /> New Client</Button>
        </div>
      } />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className="pl-10" data-testid="client-search" />
        </div>
        <Select value={tagFilter} onValueChange={setTagFilter}>
          <SelectTrigger className="w-44" data-testid="client-tag-filter"><SelectValue placeholder="Filter by tag" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tags</SelectItem>
            {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Properties</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && (<TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-10">{tagFilter !== "all" ? "No clients with this tag." : "No clients yet. Add your first customer."}</TableCell></TableRow>)}
            {shown.map((c) => (
              <TableRow key={c.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`client-row-${c.id}`} onClick={() => navigate(`/clients/${c.id}`)}>
                <TableCell>
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  {c.company && <div className="text-xs text-slate-500">{c.company}</div>}
                  {c.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.tags.map((t) => <span key={t} className="text-[10px] font-semibold bg-cyan-50 text-cyan-700 border border-cyan-100 rounded px-1.5 py-0.5">{t}</span>)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm text-slate-600 space-y-0.5">
                    {c.email && <div className="flex items-center gap-1.5"><EnvelopeSimple size={13} className="text-slate-400" />{c.email}</div>}
                    {c.phone && <div className="flex items-center gap-1.5"><Phone size={13} className="text-slate-400" />{c.phone}</div>}
                  </div>
                </TableCell>
                <TableCell>
                  {(() => {
                    const p = c.properties?.[0];
                    const addr = p ? [p.address, p.city, p.state].filter(Boolean).join(", ") : "";
                    return addr ? (
                      <div className="text-sm text-slate-600">
                        <div className="flex items-start gap-1.5">
                          <MapPin size={14} className="text-[#00B4D8] mt-0.5 shrink-0" />
                          <span>
                            {addr}
                            {c.properties.length > 1 && <span className="text-xs text-slate-400"> +{c.properties.length - 1} more</span>}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">No address</span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-[#00B4D8] p-1.5 transition-colors duration-200" data-testid={`edit-client-${c.id}`}><PencilSimple size={17} /></button>
                  <button onClick={() => remove(c)} className="text-slate-400 hover:text-rose-500 p-1.5 transition-colors duration-200" data-testid={`delete-client-${c.id}`}><Trash size={17} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={deleteAllOpen} onOpenChange={(v) => !v && setDeleteAllOpen(false)}>
        <DialogContent data-testid="delete-all-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-rose-600 flex items-center gap-2"><Trash size={20} /> Delete ALL clients?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>This permanently deletes <b>every client</b> and all their <b>quotes, jobs, invoices, and photos</b>. This cannot be undone.</p>
            <div>
              <Label>Type <span className="font-mono font-bold text-rose-600">DELETE</span> to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="mt-1.5" data-testid="delete-all-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllOpen(false)}>Cancel</Button>
            <Button onClick={deleteAll} disabled={confirmText !== "DELETE" || deleting} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-all-confirm-button">
              {deleting ? "Deleting…" : "Delete Everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClientDialog open={open} onClose={() => setOpen(false)} editing={editing} onSaved={load} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} kind="clients" onDone={load} />
    </div>
  );
}
